import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
// i18n config (Phase 3.B 2026-06-23) — must initialise before App mounts
// so navigator-detected language + <html dir> are set on first paint.
// i18n.init() runs at module-load time; `bootstrap` awaits `i18nReady`.
import { i18nReady } from "@/i18n/config";
import App from "@/App";
import { initPostHog } from "@/services/posthog";
import { initGA4 } from "@/services/ga4";
import { installTranslationSafeDom } from "@/utils/domTranslationGuard";

// Initialize Sentry for error monitoring
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || '',
  environment: process.env.NODE_ENV,
  tracesSampleRate: 0.05,

  // Scripts injected into our page by someone else's browser.
  //
  // A large share of our traffic arrives from Facebook ads and opens inside
  // FB4A's in-app WebView, which injects its own performance logger under the
  // `iabjs://` scheme. When the member closes the WebView, Facebook's Java
  // bridge is destroyed and its own script throws "Java object is gone"
  // (WEFAQNA-FRONTEND-B). It is not our code, it is not fixable by us, and it
  // recurs for every visitor from an ad.
  //
  // Matched by the injected-script URL, NOT by the message text: a real
  // postMessage failure in our own code has an https://wefaqna.com frame and
  // still reports.
  denyUrls: [/^iabjs:\/\//i],

  // Same principle, for the case where the injected frame is missing and only
  // the message survives. Deliberately ONE exact phrase, not a category: a
  // broad word like "cancelled" would substring-match real failures of ours.
  ignoreErrors: [
    'Java object is gone',
    // Firebase Auth's own internal assertion, from inside
    // `AbstractPopupRedirectOperation` (WEFAQNA-FRONTEND-E). Every frame is in
    // @firebase/auth: the auth event lands after the operation was cleaned up,
    // and the SDK rejects a promise WE never held. It arrives as an
    // `onunhandledrejection`, so `SocialLoginButton`'s try/catch structurally
    // cannot catch it -- there is no code change that would.
    //
    // Confirmed harmless before silencing it, not assumed: the two
    // identitytoolkit POSTs immediately before it returned 200, both Google
    // sign-ups in that window produced accounts, and there are ZERO
    // duplicate-email sign-ups in 14 days. Sign-up volume is unaffected
    // (46-73/day).
    //
    // The EXACT phrase, not the `INTERNAL ASSERTION FAILED` category — the SDK
    // raises other assertions that could mean something real. Same discipline
    // as the line above it.
    'INTERNAL ASSERTION FAILED: Pending promise was never set',
    // A phone browser's own autofill script, injected into every page it
    // opens: its frames are `execute_auto_fill` and `needInjectCss` at
    // `<anonymous>`, and the globals it reaches for (`xbrowser`, `swbrowser`)
    // exist only in that browser's shell (WEFAQNA-FRONTEND-N/P/Q). No name in
    // our bundle matches either, so the exact phrases cannot hide a fault of
    // ours.
    'xbrowser is not defined',
    'swbrowser is not defined',
    // The same shape on iOS (WEFAQNA-FRONTEND-Z): thrown from "global code"
    // at the page URL itself, line 1, which is where a host app's injected
    // script lands. `_G` appears nowhere in the served page, bundle,
    // config.js or error-handler.js. Both engines' spellings, exact phrase.
    "Can't find variable: _G",
    '_G is not defined',
  ],
});

// Initialize PostHog for product analytics + session replay.
// Safe no-op if REACT_APP_POSTHOG_KEY is not set.
initPostHog();

// Initialize Google Analytics 4 for SEO + funnel + Ads attribution.
// Safe no-op if gtag.js failed to load (ad-blockers, network).
initGA4();

/** Render the app, polyfilling ResizeObserver first on browsers that lack it.
 *
 * ResizeObserver arrived in Safari 13.1 (iOS 13.4). On iOS 12.5 — the last
 * release for the iPhone 6 and 5s, still in daily use across Egypt and North
 * Africa — it does not exist, and the first component to touch it throws
 * straight into the ErrorBoundary.
 *
 * That is not a cosmetic gap. A member on iOS 12.5 entered her number, received
 * the OTP (`POST` returned 200), and then the OTP input died on `ResizeObserver`
 * before she could type the code — registration could not be completed at all.
 * Four Radix packages need it too (popper, scroll-area, use-size,
 * navigation-menu), so every dropdown, select and tooltip was equally dead.
 *
 * Dynamically imported so the ~3KB only ever reaches the browsers that need it;
 * `browserslist` transpiles syntax and would never have covered a runtime API.
 * Awaited before the first render, because a component may observe on mount.
 */
async function bootstrap() {
  // Before the first render: a page translator can rewrite React's text nodes
  // at any moment, and React then removes a node that has been re-parented and
  // takes the page down with it (WEFAQNA-FRONTEND-9 — a member lost a
  // part-built groom profile that way). Must be installed before React owns any
  // DOM.
  installTranslationSafeDom();

  if (typeof window !== "undefined" && !("ResizeObserver" in window)) {
    try {
      const { ResizeObserver } = await import("@juggle/resize-observer");
      window.ResizeObserver = ResizeObserver;
    } catch (error) {
      // A failed chunk fetch must not cost everyone the app — the components
      // that need it will still throw, which is exactly today's behaviour.
      Sentry.captureException(error);
    }
  }

  // The visitor's language, when it is not bundled, is a chunk fetch. A
  // failure here still renders - in the fallback language - rather than not
  // at all.
  try {
    await i18nReady;
  } catch (error) {
    Sentry.captureException(error);
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

bootstrap();
